// ExamResultPage.tsx

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, Typography } from "@mui/material";
import type { QuizSubmissionResponse } from "@/types/Questions.types";
import McqResult from "@/components/McqResult";
import { useParams } from "react-router-dom";
import quizApi from "@/services/quizService";
import type { QuizBackground } from "@/types/Quiz.types";

interface ExamResultPageProps {
  quizNameProp?: string;
  submissionIdProp?: string;
}

export const ExamResultPage = ({ quizNameProp, submissionIdProp }: ExamResultPageProps) => {
  const { t } = useTranslation();
  let { quizName, submissionId } = useParams<{ quizName?: string; submissionId?: string }>();
  if (quizNameProp) quizName = quizNameProp;
  if (submissionIdProp) submissionId = submissionIdProp;

  const [quiz, setQuiz] = useState<QuizBackground | undefined>(undefined);
  const [quizResponse, setQuizResponse] = useState<QuizSubmissionResponse | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);

    Promise.all([
      quizName ? quizApi.getQuizByName({ quizName }) : Promise.resolve(undefined),
      submissionId ? quizApi.getSubmissionById(submissionId) : Promise.resolve(undefined),
    ])
      .then(([quizRes, submissionRes]) => {
        if (isMounted) {
          setQuiz(quizRes);
          setQuizResponse(submissionRes);
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [quizName, submissionId]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "300px",
          gap: 2,
          bgcolor: "background.default",
        }}
      >
        <CircularProgress color="primary" />
        <Typography
          sx={{
            fontSize: "1rem",
            fontWeight: 500,
            color: "text.secondary",
          }}
        >
          {t("pages.examResult.loading")}
        </Typography>
      </Box>
    );
  }

  if (!quiz || !quizResponse) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
          bgcolor: "background.default",
          minHeight: "200px",
        }}
      >
        <Typography
          sx={{
            fontSize: "1.125rem",
            fontWeight: 400,
            color: "text.secondary",
            textAlign: "center",
          }}
        >
          {t("pages.examResult.notFound")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", bgcolor: "background.default", color: "text.primary" }}>
      <McqResult
        quizName={quiz.quizName}
        paper={quiz.title}
        submissionId={quizResponse.submissionId}
        submissionData={quizResponse}
      />
    </Box>
  );
};
